import {
  Collapse,
  Divider,
  HStack,
  Icon,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import MemberIdentity from "components/MemberIdentity";
import { format } from "date-fns";
import {
  FiCheckCircle,
  FiMinusCircle,
  FiTrash,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import { MFAccessReviewWithTotals } from "state/members/accessReviews";
import MFMembers from "state/members/membersState";

const ReviewInfoSection = ({
  accessReviewWithTotal,
  accountIds,
}: {
  accessReviewWithTotal: MFAccessReviewWithTotals;
  accountIds: string[];
}) => {
  const review = accessReviewWithTotal.review;
  const { mutate } = MFMembers.useReviewAccessReviewAccounts();
  const { isOpen, onToggle } = useDisclosure();

  const markAllRequired = () => {
    mutate({
      review: "required",
      accessReviewId: accessReviewWithTotal.id,
      accessAccountIds: accountIds,
    }).then(onToggle);
  };

  return (
    <VStack
      borderRadius="12px"
      p={6}
      w="full"
      spacing={5}
      border="1px solid"
      borderColor={"gray.200"}
      shadow="md"
    >
      <HStack w="full" justify="space-between" align="flex-start">
        <VStack w="50%" align="flex-start">
          <Text variant="sm.m" color="gray.600">
            Review started
          </Text>
          <Text fontSize="sm" color={"gray.800"}>
            {format(new Date(review.startedAt), "yyyy-MM-dd")}
          </Text>
        </VStack>
        <VStack w="50%" align="flex-start">
          <Text variant="sm.m" color="gray.600">
            Status
          </Text>
          <Tag
            colorScheme={
              review.status === "in_progress"
                ? "orange"
                : review.status === "ready_for_approval"
                  ? "blue"
                  : "green"
            }
          >
            <TagLabel textTransform={"capitalize"}>
              {review.status.replaceAll("_", " ")}
            </TagLabel>
          </Tag>
        </VStack>
      </HStack>

      <HStack w="full" justify="space-between" align="flex-start">
        <VStack w="full" align="flex-start">
          <Text variant="sm.m" color="gray.600">
            Review summary
          </Text>
          <VStack w="full" spacing={2} align="flex-start">
            <HStack w="full" spacing={3}>
              <Tooltip label="Required accounts">
                <HStack spacing={1}>
                  <Icon as={FiCheckCircle} boxSize="12px" color="green.700" />
                  <Text fontSize="xs" color="gray.600">
                    {accessReviewWithTotal.required}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip label="Accounts removed">
                <HStack spacing={1}>
                  <Icon as={FiTrash} boxSize="12px" color="red.700" />
                  <Text fontSize="xs" color="gray.600">
                    {accessReviewWithTotal.removed}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip label="Accounts not required">
                <HStack spacing={1}>
                  <Icon as={FiXCircle} boxSize="12px" color="red.700" />
                  <Text fontSize="xs" color="gray.600">
                    {accessReviewWithTotal.notRequired}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip label="Out of scope">
                <HStack spacing={1}>
                  <Icon as={FiMinusCircle} boxSize="12px" color="gray.700" />
                  <Text fontSize="xs" color="gray.600">
                    {accessReviewWithTotal.outOfScope}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip label="Total number">
                <HStack spacing={1}>
                  <Icon as={FiUsers} boxSize="12px" color="blue.700" />
                  <Text fontSize="xs" color="gray.600">
                    {accessReviewWithTotal.total}
                  </Text>
                </HStack>
              </Tooltip>
            </HStack>
          </VStack>
        </VStack>
        <VStack w="full" align="flex-start">
          <Text variant="sm.m" color="gray.600">
            Assigned By
          </Text>
          {review.assignedBy && (
            <MemberIdentity member={review.assignedBy} size="sm" />
          )}
        </VStack>
      </HStack>
      <Divider />
      <HStack w="full" justify={"flex-end"}>
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          onClick={onToggle}
          isDisabled={isOpen}
        >
          Mark all required
        </Button>
      </HStack>
      <Collapse in={isOpen} style={{ width: "100%" }} animateOpacity>
        <HStack w="full" justify="flex-end">
          <Text fontSize="sm" color="gray.600">
            Are you sure?
          </Text>
          <Button size="sm" variant="subtle" tone="neutral" onClick={onToggle}>
            No
          </Button>
          <Button
            size="sm"
            variant="solid"
            tone="success"
            onClick={markAllRequired}
          >
            Yes
          </Button>
        </HStack>
      </Collapse>
    </VStack>
  );
};

export default ReviewInfoSection;
